'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslations } from 'next-intl';

import SocialButton from '@/components/SocialButton';
import Logo from '@/components/SvgIcons/Logo';
import {
  aiSolutionsMenu,
  caseStudyAccentColors,
  companyMenu,
  expertiseMenu,
  footerCaseStudySlugs,
  getInTouchMenu,
  legalLinks,
  officeMenu,
  servicesMenu,
} from '@/config/footer.config';
import { socialIconLinks } from '@/config/profiles.config';
import { projects } from '@/config/projects.config';
import { Link } from '@/i18n/navigation';
import colors from '@/styles/themes/colors';

import BrandCard from './BrandCard';
import { ShowcaseItem } from './BrandCard/CaseStudyShowcase';
import styles from './Footer.module.scss';
import FooterMenuItem from './FooterMenuItem';

const Footer = () => {
  const t = useTranslations('common');

  const slugToProject = new Map(
    projects.map(p => {
      const href = p.link?.href ?? '';
      const slug = href.replace(/^\/work\//, '');

      return [slug, p];
    })
  );

  const showcaseItems = footerCaseStudySlugs
    .map((slug): ShowcaseItem | null => {
      const project = slugToProject.get(slug);

      if (!project) {
        return null;
      }

      return {
        slug,
        title: t(project.title as 'projects.serwizz.title'),
        imageSrc: project.image.src,
        imageAlt: project.image.alt,
        accentColor: caseStudyAccentColors[slug],
      };
    })
    .filter((item): item is ShowcaseItem => item !== null);

  const caseStudyLinks = showcaseItems.map(item => ({
    text: item.title,
    href: `/work/${item.slug}`,
  }));

  return (
    <Container
      component="footer"
      maxWidth={false}
      disableGutters
      classes={{ root: styles.root }}
      className={styles.footer}
    >
      <Container maxWidth="xl" className={styles.container}>
        <Grid container spacing={{ xs: 5, lg: 4 }} className={styles.mainContent}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <BrandCard items={showcaseItems} />
          </Grid>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Grid container spacing={{ xs: 4, md: 6 }}>
              <Grid size={{ xs: 6, md: 4 }}>
                <Stack spacing={6}>
                  <FooterMenuItem {...companyMenu} />
                  <FooterMenuItem {...servicesMenu} />
                </Stack>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Stack spacing={6}>
                  <FooterMenuItem {...aiSolutionsMenu} />
                  <FooterMenuItem
                    title="footer.caseStudies"
                    links={caseStudyLinks.map(link => ({
                      text: link.text,
                      href: link.href,
                      isTranslationKey: false,
                    }))}
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Stack spacing={6}>
                  <FooterMenuItem {...expertiseMenu} />
                  <FooterMenuItem {...getInTouchMenu} />
                  <FooterMenuItem {...officeMenu} isAddress />
                </Stack>
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ display: { xs: 'block', md: 'none' } }}>
                <FooterMenuItem {...expertiseMenu} />
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ display: { xs: 'block', md: 'none' } }}>
                <FooterMenuItem {...getInTouchMenu} />
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', md: 'none' } }}>
                <FooterMenuItem {...officeMenu} isAddress />
              </Grid>
            </Grid>
            <Stack spacing={3} pt={{ xs: 8, md: 6 }}>
              <Stack direction="row" gap={1.5} flexWrap="wrap" className={styles.socialLinks}>
                {socialIconLinks.map(({ name, href, icon }) => (
                  <SocialButton key={name} name={name} href={href} icon={icon} />
                ))}
              </Stack>
              <Typography variant="body1" component="p" color={colors.graytone200} className={styles.description}>
                {t('footer.description')}
              </Typography>
              <Stack direction="row" gap={3} flexWrap="wrap">
                {legalLinks.map(link => (
                  <Link key={link.href} href={link.href} className={styles.legalLink}>
                    <Typography variant="body1" component="span" color={colors.white}>
                      {t(link.labelKey)}
                    </Typography>
                  </Link>
                ))}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <Box className={styles.watermarkWrapper} aria-hidden>
          <Box className={styles.watermark} aria-hidden>
            <Logo width="100%" height="100%" mainColor={colors.graytone400} secondaryColor={colors.graytone800} />
          </Box>
        </Box>
      </Container>
    </Container>
  );
};

export default Footer;
